import { useQuery } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import {
  clinicBankAccountApi,
  clinicBankAccountQueryKeys,
} from "../../../shared/api/clinicBankAccount";
import { apiError } from "../../../shared/api/client";
import { formatVndWithUnit } from "../../../shared/format";

/**
 * Mã QR để bệnh nhân chuyển khoản trả một hoá đơn (ADR-0050).
 *
 * <p>Tiền đi THẲNG từ bệnh nhân vào tài khoản phòng khám. Addy dựng mã và không giữ đồng nào.
 *
 * <p><b>Mã được PHÁT HÀNH, không phải dựng tạm (#18998).</b> Trước đây màn này hỏi server "dựng
 * cho tôi một mã với số tiền này và nội dung này" và không ai ghi lại gì. Giờ nó nói "phát hành mã
 * thu tiền cho hoá đơn này", và server lưu lại mã ấy trỏ vào tài khoản nào tại thời điểm đó. Nhờ
 * vậy, khi tiền về, hệ thống đối chiếu được với điều bệnh nhân đã NHÌN THẤY, thay vì với cấu hình
 * hiện tại của phòng khám — hai thứ có thể đã khác nhau.
 *
 * <p><b>Mã luôn ghi TRỌN phần còn phải thu</b>, kể cả khi thu ngân đang gõ một số tiền nhỏ hơn ở ô
 * bên dưới. Chuyển khoản lệch số tiền trên mã sẽ không được tự ghi nhận mà vào hàng đợi cho người
 * xem — nên hứa hẹn một con số khác với con số trên mã là dẫn thẳng vào đó.
 *
 * <p>Addy KHÔNG biết tiền đã về hay chưa nếu phòng khám chưa nối báo có từ ngân hàng. Một màn hình
 * hiện "đã thanh toán" chỉ vì mã QR đã được mở ra là thứ tệ hơn cả không có mã.
 */
export function TransferQr({ invoiceId }: { invoiceId: string }) {
  const qr = useQuery({
    queryKey: clinicBankAccountQueryKeys.paymentQr(invoiceId),
    queryFn: () => clinicBankAccountApi.paymentQr(invoiceId),
    // Phát hành là idempotent phía server, nhưng vẫn giữ kết quả để mở lại hộp thoại không gọi
    // lại: mã không đổi, và một request thừa trên đường thanh toán không đem lại gì.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (qr.isPending) {
    return <p className="text-sm text-muted-foreground">Đang tạo mã…</p>;
  }
  if (qr.isError) {
    // Chưa khai tài khoản là ca thường gặp nhất, và nó sửa được — nên nói luôn sửa ở đâu.
    return (
      <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
        {apiError(qr.error).detail}
      </p>
    );
  }
  if (!qr.data) return null;

  return (
    <div className="space-y-2 rounded-md border border-input p-3">
      <div className="flex justify-center rounded-md bg-white p-2">
        <QRCodeSVG value={qr.data.qrPayload} size={172} level="M" />
      </div>
      <dl className="space-y-0.5 text-sm">
        <Row label="Ngân hàng" value={qr.data.bankShortName} />
        <Row label="Số tài khoản" value={qr.data.accountNo} mono />
        <Row label="Chủ tài khoản" value={qr.data.accountName} />
        <Row label="Số tiền" value={formatVndWithUnit(qr.data.amountVnd)} mono />
        <Row label="Nội dung" value={qr.data.transferCode} mono />
      </dl>
      <p className="text-xs text-muted-foreground">
        Bệnh nhân chuyển thẳng vào tài khoản phòng khám. Mã này ghi trọn phần còn phải thu —
        chuyển khác số tiền hoặc khác nội dung sẽ phải đối chiếu bằng tay.
      </p>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className={`min-w-0 truncate font-medium ${mono ? "tabular-nums" : ""}`} title={value}>
        {value}
      </dd>
    </div>
  );
}
